import { z } from 'zod';

/**
 * Connections (owner's request, 2026-10-30): the outside providers a company's 212Ez talks to,
 * each with that company's own account. Secrets are stored encrypted, never sent back to the
 * browser (only "set, ending 1234") and never written to a log. A service without an Active
 * connection is simulated on a developer machine and in the tests; on the live server, once the
 * release switch `no-simulation` is on, it is refused with a message naming the service.
 *
 * The carrier (MVNE) is connected per carrier on the same page once its connector is built;
 * each MVNE has its own API, given under its contract.
 */
export const CONNECTION_SERVICES = [
  'sms',
  'email',
  'address',
  'shipping',
  'payments',
  'tax',
  'lifeline',
] as const;
export type ConnectionService = (typeof CONNECTION_SERVICES)[number];

export const SERVICE_LABELS: Record<ConnectionService, string> = {
  sms: 'SMS and voice calls',
  email: 'E-mail',
  address: 'Address check',
  shipping: 'Shipping labels',
  payments: 'Card payments',
  tax: 'Telecom tax',
  lifeline: 'Lifeline (NLAD)',
};

/** What each service does in 212Ez, shown on the Connections page. */
export const SERVICE_USES: Record<ConnectionService, string> = {
  sms: 'Customer SMS and IVR notifications, campaigns, reminders.',
  email: 'Customer e-mails, password-reset links, Monitoring Panel alerts.',
  address: 'Checks and standardises the address of every enrollment (USPS API Log).',
  shipping:
    'Buys the label and tracking number of a shipped order (Shipping Queue, Bulk Print Label).',
  payments: 'Card payments, saved cards, Auto Pay by card and card refunds.',
  tax: "Telecom taxes and fees on every charge, by the customer's address.",
  lifeline:
    'Checks a Lifeline applicant with the National Verifier, and enrolls, updates and de-enrolls subscribers in NLAD.',
};

export const CONNECTION_MODES = ['Sandbox', 'Live'] as const;
export type ConnectionMode = (typeof CONNECTION_MODES)[number];

export interface ProviderField {
  key: string;
  label: string;
  /** Stored encrypted; the page shows only whether it is set and its last four characters. */
  secret: boolean;
  required: boolean;
  help?: string;
  /** A fixed list of choices instead of free text. */
  options?: readonly string[];
}

export interface ProviderDef {
  key: string;
  service: ConnectionService;
  label: string;
  fields: ProviderField[];
  /** What Sandbox means for this provider. */
  sandbox: string;
  /** Where the account holder finds the values. */
  where: string;
}

const f = (
  key: string,
  label: string,
  opts: Partial<Omit<ProviderField, 'key' | 'label'>> = {},
): ProviderField => ({ key, label, secret: false, required: true, ...opts });

export const PROVIDERS: readonly ProviderDef[] = [
  {
    key: 'twilio',
    service: 'sms',
    label: 'Twilio',
    fields: [
      f('accountSid', 'Account SID', { help: 'Starts with AC.' }),
      f('authToken', 'Auth Token', { secret: true }),
      f('fromNumber', 'From number', {
        help: 'Your Twilio phone number, 10 digits or +1 and 10 digits. Sends SMS and makes IVR calls.',
      }),
    ],
    sandbox:
      'Sandbox uses your Twilio test credentials: Twilio accepts the request and sends nothing.',
    where: 'Twilio Console > Account > API keys & tokens; the number under Phone Numbers.',
  },
  {
    key: 'sendgrid',
    service: 'email',
    label: 'SendGrid',
    fields: [
      f('apiKey', 'API Key', { secret: true, help: 'Needs the "Mail Send" permission.' }),
      f('fromEmail', 'From e-mail', {
        help: 'A sender verified in SendGrid. Used when a template has no From Email of its own.',
      }),
      f('fromName', 'From name', { required: false }),
    ],
    sandbox: 'Sandbox turns on SendGrid sandbox mode: the e-mail is checked and not delivered.',
    where: 'SendGrid > Settings > API Keys; the sender under Sender Authentication.',
  },
  {
    key: 'usps',
    service: 'address',
    label: 'USPS Addresses API',
    fields: [
      f('clientId', 'Consumer Key', { secret: true }),
      f('clientSecret', 'Consumer Secret', { secret: true }),
    ],
    sandbox: 'Sandbox calls the USPS test environment (apis-tem.usps.com).',
    where: 'USPS Developer Portal (developer.usps.com) > Apps > your app.',
  },
  {
    key: 'shipstation',
    service: 'shipping',
    label: 'ShipStation',
    fields: [
      f('apiKey', 'API Key', { secret: true }),
      f('apiSecret', 'API Secret', { secret: true }),
      f('carrierCode', 'Carrier code', {
        help: 'The ShipStation code of the carrier account to buy labels from, e.g. stamps_com, ups, fedex.',
      }),
      f('serviceCode', 'Service code', {
        help: 'e.g. usps_first_class_mail, usps_priority_mail, ups_ground.',
      }),
      f('packageCode', 'Package code', { required: false, help: 'Default: package.' }),
      f('weightOz', 'Weight (oz)', { help: 'Weight of one SIM or phone parcel, e.g. 2 or 16.' }),
    ],
    sandbox: 'Sandbox buys test labels (testLabel): nothing is charged and nothing ships.',
    where: 'ShipStation > Settings > Account > API Settings.',
  },
  {
    key: 'stripe',
    service: 'payments',
    label: 'Stripe',
    fields: [
      f('publishableKey', 'Publishable key', { help: 'Starts with pk_test_ or pk_live_.' }),
      f('secretKey', 'Secret key', { secret: true, help: 'Starts with sk_test_ or sk_live_.' }),
    ],
    sandbox: 'Sandbox uses your Stripe test keys: test card numbers only, no money moves.',
    where: 'Stripe Dashboard > Developers > API keys.',
  },
  {
    key: 'authorizenet',
    service: 'payments',
    label: 'Authorize.Net',
    fields: [
      f('apiLoginId', 'API Login ID'),
      f('transactionKey', 'Transaction Key', { secret: true }),
      f('clientKey', 'Public Client Key', {
        help: "Used by Authorize.Net's card form in the browser; the card never reaches 212Ez.",
      }),
    ],
    sandbox: 'Sandbox uses an Authorize.Net sandbox account: test cards only, no money moves.',
    where:
      'Authorize.Net Merchant Interface > Account > API Credentials & Keys (Login ID, Transaction Key) and Manage Public Client Key.',
  },
  {
    key: 'ceretax',
    service: 'tax',
    label: 'CereTax',
    fields: [
      f('apiKey', 'API key', { secret: true }),
      f('profileId', 'Profile ID'),
      f('psCode', 'PS code for wireless service', {
        help: 'For a plan with no tax codes of its own (Plan Config sets them per plan).',
      }),
      f('psCodeTopup', 'PS code for top-ups', {
        required: false,
        help: 'Empty: the service PS code.',
      }),
      f('psCodeEquipment', 'PS code for equipment', {
        required: false,
        help: 'Phones and accessories; empty: the service PS code.',
      }),
      f('psCodeLateFee', 'PS code for late and restore fees', {
        required: false,
        help: 'Empty: fees are taxed with the charge they are on.',
      }),
      f('businessType', 'Business type', {
        options: ['14 Prepaid Wireless', '12 CMRS/Wireless'],
        help: "For a charge with no plan; a plan's own business type comes first.",
      }),
    ],
    sandbox:
      'Sandbox calls the CereTax CERT environment (calc.cert.ceretax.net) with your CERT API key.',
    where:
      'CereTax gives the API key and profile ID when the account is set up (your Implementation Specialist).',
  },
  {
    key: 'usac',
    service: 'lifeline',
    label: 'USAC NLAD API',
    fields: [
      f('apiUrl', 'API address', {
        help: 'The address USAC gave you, ending in /svc/1. Sandbox uses its staging address.',
      }),
      f('username', 'API username'),
      f('password', 'API password', { secret: true }),
      f('sac', 'SAC', {
        help: 'Your study area code, or one per state: OH=123456,FL=654321',
      }),
      f('repId', 'Rep ID', {
        required: false,
        help: 'Your Representative Accountability Database ID. Empty sends "not assisted".',
      }),
    ],
    sandbox: 'Sandbox uses the staging API address USAC gives you.',
    where:
      'Your ETC Administrator creates the API account in NLAD on USAC One Portal. Its production use needs the Interconnection Security Agreement USAC holds on file.',
  },
];

export const providersOf = (service: ConnectionService) =>
  PROVIDERS.filter((p) => p.service === service);
export const providerDef = (key: string) => PROVIDERS.find((p) => p.key === key);

export const CONNECTION_STATUSES = ['Active', 'Inactive'] as const;

/**
 * Saving a connection. A secret left empty keeps the stored one; the server checks the fields
 * of the provider chosen and names each one that is missing.
 */
export const connectionInputSchema = z.object({
  provider: z.string().min(1, 'Select Provider'),
  mode: z.enum(CONNECTION_MODES, { errorMap: () => ({ message: 'Select Mode' }) }),
  status: z.enum(CONNECTION_STATUSES).default('Active'),
  values: z.record(z.string().max(500)).default({}),
});
export type ConnectionInput = z.input<typeof connectionInputSchema>;

export interface ConnectionDto {
  service: ConnectionService;
  /** Null when the service has never been connected. */
  provider: string | null;
  mode: ConnectionMode | null;
  status: 'Active' | 'Inactive' | null;
  /** Plain fields as stored; secret fields as "set, ending 1234" or "". */
  values: Record<string, string>;
  lastTest: { at: string; ok: boolean; message: string } | null;
  updatedAt: string | null;
  updatedBy: string;
  /** What happens to this service now: "Connected", "Simulated" or "Not connected". */
  state: 'Connected' | 'Simulated' | 'Not connected';
}

export interface ConnectionTestDto {
  ok: boolean;
  message: string;
}

/* --------------------------------- Carriers (MVNE) -------------------------------- */

/** An MVNE connector 212Ez has, as the Connections page offers it. */
export interface MvneConnectorDto {
  key: string;
  label: string;
  fields: ProviderField[];
  sandbox: string;
  where: string;
}

/** One carrier of the company (Plan Config > Carrier Setup) and its MVNE connection. */
export interface CarrierConnectionDto {
  carrierId: string;
  carrierCode: string;
  carrierName: string;
  connector: string | null;
  mode: ConnectionMode | null;
  status: 'Active' | 'Inactive' | null;
  values: Record<string, string>;
  lastTest: { at: string; ok: boolean; message: string } | null;
  /** The address the MVNE posts its callbacks to (shown to administrators only). */
  callbackUrl: string | null;
  updatedAt: string | null;
  updatedBy: string;
  state: 'Connected' | 'Simulated' | 'Not connected';
}

export interface CarrierConnectionsDto {
  connectors: MvneConnectorDto[];
  carriers: CarrierConnectionDto[];
}

export const carrierConnectionInputSchema = z.object({
  connector: z.string().min(1, 'Select MVNE'),
  mode: z.enum(CONNECTION_MODES, { errorMap: () => ({ message: 'Select Mode' }) }),
  status: z.enum(CONNECTION_STATUSES).default('Active'),
  values: z.record(z.string().max(2000)).default({}),
});
export type CarrierConnectionInput = z.input<typeof carrierConnectionInputSchema>;

/** One callback an MVNE posted, as the Connections page lists them. */
export interface CarrierEventDto {
  id: string;
  receivedAt: string;
  status: 'Stored' | 'Applied' | 'Ignored' | 'Failed';
  note: string;
  /** The start of the body, for a look; the full body stays on the server. */
  preview: string;
}

/* ---------------------------------- Go-live checklist ------------------------------- */

/** One line of the go-live checklist of Settings > Connections. */
export interface ReadinessItemDto {
  key: string;
  label: string;
  /** ok: done; todo: needed before real customers; info: worth knowing. */
  status: 'ok' | 'todo' | 'info';
  detail: string;
  /** The Get started step it belongs to (`ONBOARDING_STEPS`). */
  step: 'carriers' | 'selling' | 'services' | 'people' | 'customers' | 'platform' | 'live';
  /** The page where it is done. */
  link: string;
}
